<template>
  <aside
    class="bottombar flex justify-between items-center animate noselect"
    :aria-label="t('readerProgressionBar.title')"
    :class="[
      !visibilityStore.showUI ? 'hidden-bar' : null,
      (wpsClient.media.isPDF || wpsClient.media.isFXL) &&
      !wpsClient.interactive &&
      !attributesStore.isMobile &&
      !wpsClient.media.isMediaOverlay
        ? 'fxl-bar'
        : null,
      (wpsClient.media.isPDF || wpsClient.media.isFXL) && !wpsClient.interactive
        ? 'bottombar--tts-bar'
        : null,
      wpsClient.interactive && !visibilityStore.showUI
        ? 'bottombar--interactive-hidden'
        : null,
      wpsClient.media.isMediaOverlay ? 'bottombar--mediaOverlay' : null,
    ]"
    @click="
      attributesStore.screenHasHover || wpsClient.interactive
        ? null
        : visibilityStore.setShowUI(true)
    "
  >
    <AccessibleButton
      class="square-icon-button bottombar__back-button"
      :class="{ ['button-disabled']: !navigator.hasBack }"
      :disabled="!navigator.hasBack"
      :title="t('readerProgressionBar.arrowBack')"
      :aria-label="t('readerProgressionBar.arrowBack')"
      @click="navigator.goBack()"
    >
      <template #icon>
        <span class="material-icons" aria-hidden="true">undo</span>
      </template>
    </AccessibleButton>

    <div
      class="flex-auto mx-5 mt-1 bottombar__slider-container"
      :class="{
        ['bottombar__slider-container--hidden']: !visibilityStore.showUI,
        ['vertical']: props.verticalMode,
      }"
    >
      <InputSlider
        :container-label="t('readerProgressionBar.slider')"
        :input-label="t('readerProgressionBar.readerProgression')"
        :label-at-position="buildLabelAt"
        :range-max="Math.max(navigator.positions?.length || 1, 1) - 1"
        :vertical="props.verticalMode"
        :index="currentPosition"
        @update:index="updatePosition"
      />
      <span
        v-if="!verticalMode"
        class="w-full inline-block text-center text-[0.95rem]"
        :class="
          wpsClient.media.isPDF && !attributesStore.isMobile
            ? 'position-text-pdf'
            : null
        "
      >
        <span class="sr-only"> {{ t("reader.currentPosition") }}</span>
        {{ navigator.positionIndicator || "1" }}
        {{ t("readerProgressionBar.positionFragment") }}
        {{ navigator.positions.length || 1 }}
      </span>
    </div>
    <AccessibleButton
      class="square-icon-button bottombar__forward-button"
      :class="{ ['button-disabled']: !navigator.hasForward }"
      :disabled="!navigator.hasForward"
      :title="t('readerProgressionBar.arrowForward')"
      :aria-label="t('readerProgressionBar.arrowForward')"
      @click="navigator.goForward()"
    >
      <template #icon>
        <span class="material-icons" aria-hidden="true">redo</span>
      </template>
    </AccessibleButton>
  </aside>
</template>

<script setup lang="ts">
import { computed, inject, Ref } from "vue";
import { useI18n } from "vue-i18n";

import { Link, Locator } from "@readium/shared";

import AccessibleButton from "@/components/shared/AccessibleButton.vue";
import { useAttributesStore } from "@/stores/attributes";
import { useUiVisibilityStore } from "@/stores/uiVisibility";

import { useWpsClient } from "@/wps/client";

import InputSlider from "./InputSlider.vue";

import { useNavigatorWithHistory } from "../navigator";
import { TocItem } from "../navigator/types";
import MediaPlayer from "@/services/media/player";

const { t } = useI18n();

const wpsClient = useWpsClient();

const attributesStore = useAttributesStore();
const visibilityStore = useUiVisibilityStore();
const navigator = useNavigatorWithHistory();
const mediaPlayer = inject("mediaPlayer") as Ref<MediaPlayer>;

const props = defineProps<{ verticalMode: boolean }>();

const currentPosition = computed(() => {
  if (wpsClient.media.isPDF) {
    return (navigator.activeLocator?.locations.page() || 1) - 1;
  }
  return (navigator.activeLocator?.locations.position || 1) - 1;
});

async function updatePosition(index: number) {
  if (mediaPlayer.value) {
    mediaPlayer.value.stop();
  }
  if (wpsClient.media.isPDF) {
    return await navigator.go(index + 1);
  }

  const newPosition = navigator.positions[index];
  if (!newPosition) return;

  await navigator.go(newPosition);
}

function buildLabelAt(index: number): string {
  const title = findTitleAtPosition(index);

  if (title) {
    return `${title} - ${index + 1}`;
  }

  return `${index + 1}`;
}

function findTocItemAtPage(
  page: number | Locator,
  items: Readonly<TocItem[]>
): TocItem | null {
  for (const item of items) {
    if (typeof page === "number") {
      if (item.target === page) return item;
    } else {
      const href = page.href;
      if (item.target instanceof Locator) {
        if (item.href === href) return item;
      } else if (item.target instanceof Link) {
        if (item.href === href) return item;
      }
    }
    if (item.children) {
      const childItem = findTocItemAtPage(page, item.children);
      if (childItem) {
        return childItem;
      }
    }
  }
  return null;
}

function findTitleAtPosition(index: number): string {
  if (!navigator.tableOfContents?.length) {
    return null;
  }

  if (wpsClient.media.isPDF) {
    const item = findTocItemAtPage(index + 1, navigator.tableOfContents);

    return item ? item.title : null;
  }

  if (navigator.positions.length === 0) {
    return null;
  }

  const position = navigator.positions[index];
  return findTocItemAtPage(position, navigator.tableOfContents)?.title ?? null;
}
</script>

<style lang="scss" scoped>
// imported reader instead variables because here is used .square-icon-button
@use "@/assets/styles/reader.scss" as *;

.bottombar {
  bottom: var(--safe-area-b);
  z-index: 2;
  .button-group {
    margin-left: $spacingM;
  }
  .square-icon-button {
    opacity: 0;
  }
  &.animate {
    .square-icon-button {
      opacity: 1;
      transition: opacity 0.2s cubic-bezier(0, 1, 0.5, 1);
    }
  }

  &__slider-container {
    &--hidden {
      display: none;
    }

    &.vertical {
      margin: 0.5rem;
      width: 100%;
      justify-items: center;
    }
  }

  &--interactive-hidden {
    display: none;
  }
}
</style>
